import { PropsWithChildren, useEffect } from "react";

import { authStore, type AuthSessionSnapshot } from "../auth/authStore";
import { getSelfProfile, normalizeSupportedLocale, type SupportedLocale } from "../profile/profileApi";
import { DEFAULT_WEB_LOCALE, applyRuntimeLocale } from "./i18n";

interface LocaleSyncAuthStore {
  getSnapshot(): AuthSessionSnapshot;
  subscribe(listener: (snapshot: AuthSessionSnapshot) => void): () => void;
}

interface LocaleSyncDependencies {
  authStore: LocaleSyncAuthStore;
  applyLocale: (locale: SupportedLocale) => void | Promise<void>;
  readProfileLocale: () => Promise<SupportedLocale>;
}

function hasAuthenticatedSession(snapshot: AuthSessionSnapshot): boolean {
  return snapshot.accessToken !== null && snapshot.refreshToken !== null;
}

export function startLocaleSync(dependencies: LocaleSyncDependencies): () => void {
  let stopped = false;
  let runID = 0;

  const applyDefaultLocale = () => {
    void dependencies.applyLocale(DEFAULT_WEB_LOCALE);
  };

  const syncWithSnapshot = (snapshot: AuthSessionSnapshot) => {
    runID += 1;
    const currentRunID = runID;

    if (!hasAuthenticatedSession(snapshot)) {
      applyDefaultLocale();
      return;
    }

    void dependencies
      .readProfileLocale()
      .then((locale) => {
        if (stopped || currentRunID !== runID) {
          return;
        }
        void dependencies.applyLocale(locale);
      })
      .catch(() => {
        if (stopped || currentRunID !== runID) {
          return;
        }
        applyDefaultLocale();
      });
  };

  syncWithSnapshot(dependencies.authStore.getSnapshot());
  const unsubscribe = dependencies.authStore.subscribe((snapshot) => {
    syncWithSnapshot(snapshot);
  });

  return () => {
    stopped = true;
    runID += 1;
    unsubscribe();
  };
}

export function LocaleProvider({ children }: PropsWithChildren) {
  useEffect(() => {
    const stop = startLocaleSync({
      authStore,
      applyLocale: (locale) => applyRuntimeLocale(locale),
      readProfileLocale: async () => {
        const profile = await getSelfProfile();
        return normalizeSupportedLocale(profile.locale);
      },
    });

    return stop;
  }, []);

  return <>{children}</>;
}
